/**
 * Карточка строки плана справа: статус строки (Активно / Отменён / Пересорт),
 * пометка к нему, отдельный комментарий и лента комментариев строки и партии.
 * Спека `docs/specs/2026-11-04-plan-line-status-design.md`, «Статус строки»,
 * «Комментарий строки», «Экран «Планы»».
 *
 * ОДНА НА ДВА ЭКРАНА: «Планы» открывают её одной строкой состава, карточка
 * папки — всеми строками размера (по партиям их бывает несколько). Поэтому
 * панель пишет САМА и отдаёт ответ наверх (`onSaved`), а данные правит хозяин:
 * второй копии строк здесь нет, и после записи панель рисует то, что хозяин
 * положил в `lines`.
 *
 * «Создан Заказ» и «Отгружен» вручную не ставим (их ставят заказы и крон
 * areserv): кнопок таких нет, а строка в них — с погашенными кнопками и
 * причиной словами. Причина видна ТЕКСТОМ, не только подсказкой: на планшете
 * наведения нет.
 */

import { type ReactNode, useState } from "react";

import { SidePanel } from "@/components/layout/SidePanel";
import { ApiFailure, apiFetch } from "@/lib/api";
import { nf } from "@/lib/format";
import { Badge } from "./Badge";
import { CommentFeed } from "./PlanComments";
import { LINE_STATUS_TONE } from "./style";
import type { LineStatusSaved, PlanComment } from "./types";

export type PanelLine = {
  line_id: number;
  plan_id: number;
  size: string | null;
  /** Чья строка — модель: нужна, когда в панели строки многих размеров и моделей (карточка папки). */
  label?: string | null;
  qty: number;
  ordered: number;
  status_id: number;
  status_name: string | null;
  comments: PlanComment[];
  /** Статус партии строки: не «Активный» — состав не правим (как правку числа). */
  batch_status_id: number;
};

/** Что записано: хозяин правит свою строку и итоги партии. */
export type PanelSaved =
  | { kind: "status"; line_id: number; plan_id: number; result: LineStatusSaved }
  | { kind: "comment"; line_id: number; plan_id: number; comment: PlanComment };

/** Ручные переходы — те же, что `LINE_MANUAL_MOVES` сервера: 1 ↔ 4, 1 ↔ 5. */
const КНОПКИ = [
  { status: 1, label: "Активно", from: [4, 5] },
  { status: 4, label: "Отменён — не приедет", from: [1] },
  { status: 5, label: "Пересорт", from: [1] },
] as const;

const COMMENT_MAX = 500;

const ЗАКРОЕТСЯ = "партия закроется сама: остальное в заказе (крон areserv, до часа)";

/** Те же слова, что отказ сервера `PLAN_QTY_ZERO`. */
const НОЛЬ_ПЛАНА = "строку сняли нулём — вернуть её можно только числом плана в areserv";

/** Почему статус строки не сменить вовсе; `null` — можно. */
function закрыто(line: PanelLine, writeBlocked: string | null): string | null {
  if (line.status_id === 2 || line.status_id === 3) return "в заказе / отгружена — статус ставят заказы";
  if (line.batch_status_id !== 1) return "партия закрыта — её состав не правим";
  return writeBlocked;
}

export function PlanLinePanel({
  lines,
  batchComments,
  writeBlocked,
  title,
  actions,
  onClose,
  onSaved,
}: {
  lines: PanelLine[];
  /** Комментарии areserv к партии (партиям) строк — только чтение. */
  batchComments: PlanComment[];
  /** Почему запись в areserv закрыта, словами; `null` — открыта. */
  writeBlocked: string | null;
  /** Подпись шапки; нет — «Строка плана N». */
  title?: string;
  /** Тулбар шапки справа — например, меню «Открыть» и ссылка в «Планы». */
  actions?: ReactNode;
  onClose: () => void;
  onSaved: (saved: PanelSaved) => void;
}) {
  const first = lines[0];
  return (
    <SidePanel
      title={title ?? (first ? `Строка плана ${first.line_id} · партия ${first.plan_id}` : "Строка плана")}
      width="w-[min(480px,94vw)]"
      actions={actions}
      portal
      annot="карточка строки плана"
      onClose={onClose}
    >
      <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-y-auto p-4 text-[12.5px]">
        {lines.length === 0 ? <div className="text-fg-muted">Строки нет в составе — перечитайте экран.</div> : null}
        {lines.map((line) => (
          <LineSection key={line.line_id} line={line} writeBlocked={writeBlocked} onSaved={onSaved} />
        ))}
        <section className="flex flex-col gap-1.5" data-annot="комментарии партии areserv">
          <h3 className="text-[12px] font-medium text-fg-muted">Комментарии партии · areserv</h3>
          <CommentFeed items={batchComments} empty="В areserv к партии комментариев нет." />
        </section>
      </div>
    </SidePanel>
  );
}

function LineSection({
  line,
  writeBlocked,
  onSaved,
}: {
  line: PanelLine;
  writeBlocked: string | null;
  onSaved: (saved: PanelSaved) => void;
}) {
  const [текст, setТекст] = useState("");
  const [идёт, setИдёт] = useState(false);
  const [беда, setБеда] = useState<string | null>(null);
  const [итог, setИтог] = useState<string | null>(null);
  const причина = закрыто(line, writeBlocked);
  // Число 0 в «Активно»/«Пересорт» — невидимая строка, держащая партию открытой
  // (сервер отказывает `PLAN_QTY_ZERO`); вернуть её можно только числом.
  const ноль = line.qty === 0 ? НОЛЬ_ПЛАНА : null;

  const сменить = async (status: number) => {
    setИдёт(true);
    setБеда(null);
    setИтог(null);
    try {
      const заметка = текст.trim();
      const result = await apiFetch<LineStatusSaved>(`/api/plans/lines/${line.line_id}/status`, {
        method: "POST",
        body: JSON.stringify(заметка ? { status_id: status, comment: заметка } : { status_id: status }),
      });
      setТекст("");
      setИтог(
        result.batch_will_close ? ЗАКРОЕТСЯ : result.changed ? null : `статус уже «${result.status_name}»`,
      );
      onSaved({ kind: "status", line_id: line.line_id, plan_id: line.plan_id, result });
    } catch (error) {
      setБеда(error instanceof ApiFailure ? error.message : "areserv не ответила");
    } finally {
      setИдёт(false);
    }
  };

  const добавить = async () => {
    const заметка = текст.trim();
    if (!заметка) return;
    setИдёт(true);
    setБеда(null);
    setИтог(null);
    try {
      const done = await apiFetch<{ line_id: number; comment: PlanComment }>(
        `/api/plans/lines/${line.line_id}/comment`,
        { method: "POST", body: JSON.stringify({ text: заметка }) },
      );
      setТекст("");
      onSaved({ kind: "comment", line_id: line.line_id, plan_id: line.plan_id, comment: done.comment });
    } catch (error) {
      setБеда(error instanceof ApiFailure ? error.message : "не получилось записать комментарий");
    } finally {
      setИдёт(false);
    }
  };

  return (
    <section
      className="flex flex-col gap-2 rounded border border-border bg-bg-subtle p-3"
      data-annot={`строка плана ${line.line_id}`}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-[13px] font-semibold text-fg">размер {line.size ?? "—"}</span>
        {line.label ? <span className="text-fg-muted">{line.label}</span> : null}
        <span className="tnum text-fg-muted">план {nf.format(line.qty)}</span>
        <span className="tnum text-fg-muted">заказано {nf.format(line.ordered)}</span>
        <span className="ml-auto">
          <Badge tone={LINE_STATUS_TONE[line.status_id]} text={line.status_name} />
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5" role="group" aria-label="статус строки">
        {КНОПКИ.map((b) => {
          const сейчас = line.status_id === b.status;
          const нольЗдесь = b.status === 4 ? null : ноль;
          const можно =
            !причина && !нольЗдесь && !идёт && (b.from as readonly number[]).includes(line.status_id);
          const почему = сейчас
            ? "строка уже в этом статусе"
            : (причина ??
              нольЗдесь ??
              (можно || идёт ? undefined : `из «${line.status_name ?? "—"}» сюда вручную не переводим`));
          return (
            <button
              key={b.status}
              type="button"
              disabled={!можно}
              title={почему}
              onClick={() => void сменить(b.status)}
              className={
                "h-7 rounded-[var(--radius-ui)] border px-2.5 " +
                (сейчас
                  ? "border-accent bg-accent-dim text-fg"
                  : "border-border bg-bg text-fg hover:border-accent hover:text-accent") +
                " disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-fg"
              }
            >
              {b.label}
              {сейчас ? " · сейчас" : ""}
            </button>
          );
        })}
      </div>
      {причина ? (
        <div className="text-fg-muted">Статус не сменить: {причина}.</div>
      ) : ноль ? (
        <div className="text-fg-muted">«Активно» и «Пересорт» закрыты: {ноль}.</div>
      ) : null}

      <textarea
        value={текст}
        onChange={(e) => setТекст(e.target.value)}
        placeholder="Комментарий — необязательно: уйдёт вместе со статусом или кнопкой «добавить комментарий»"
        rows={2}
        disabled={идёт}
        maxLength={COMMENT_MAX}
        className="rounded border border-border bg-bg px-2 py-1"
        aria-label="комментарий к строке"
      />
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={идёт || текст.trim() === ""}
          onClick={() => void добавить()}
          className="rounded bg-fg px-3 py-1 text-bg disabled:cursor-not-allowed disabled:opacity-40"
        >
          {идёт ? "пишем…" : "Добавить комментарий"}
        </button>
        <span className="ml-auto text-fg-faint">
          {текст.length}/{COMMENT_MAX} · комментарий хранит supply, в areserv не уходит
        </span>
      </div>
      {беда ? <div className="text-err">{беда}</div> : null}
      {итог ? <div className="text-warn">{итог}</div> : null}

      <div className="flex flex-col gap-1.5 pt-1">
        <h3 className="text-[12px] font-medium text-fg-muted">Комментарии строки</h3>
        <CommentFeed items={line.comments} empty="Комментариев к строке нет." />
      </div>
    </section>
  );
}
